import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import { Fragment, useState } from 'react';
import Spacer from '../Spacer';

export interface KeyValuePair<TKey extends string | number> {
    key: TKey;
    value: string;
    /** Optional heading to file this item under. When no item has one, the list renders flat. */
    group?: string;
}

export interface TransferListProps<TKey extends string | number> {
    left: KeyValuePair<TKey>[];
    setLeft: (items: KeyValuePair<TKey>[]) => void;
    right: KeyValuePair<TKey>[];
    setRight: (items: KeyValuePair<TKey>[]) => void;
    listWidth?: number;
    listHeight?: number;
}

const TransferList = <TKey extends string | number>({ left = [], setLeft, right = [], setRight, listWidth = 200, listHeight = 230 }: TransferListProps<TKey>) => {
    const [checked, setChecked] = useState<KeyValuePair<TKey>[]>([]);
   
    const not = <TKey extends string | number>(a: KeyValuePair<TKey>[], b: KeyValuePair<TKey>[]) =>
        a.filter(item => !b.some(bi => bi.key === item.key));

    const intersection = <TKey extends string | number>(a: KeyValuePair<TKey>[], b: KeyValuePair<TKey>[]) =>
        a.filter(item => b.some(bi => bi.key === item.key));

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    const handleToggle = (item: KeyValuePair<TKey>) => () => {
        const currentIndex = checked.findIndex(i => i.key === item.key);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(item);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleAllRight = () => {
        setRight(right.concat(left));
        setLeft([]);
    };

    const handleCheckedRight = () => {
        setRight(right.concat(leftChecked));
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    const handleAllLeft = () => {
        setLeft(left.concat(right));
        setRight([]);
    };

    // Items are rebuilt on every render, so identity has to be compared by key rather than
    // by reference — otherwise a checked item stops looking checked as soon as it re-renders.
    const isChecked = (item: KeyValuePair<TKey>) => checked.some(c => c.key === item.key);

    // Preserves the order the caller supplied, for both the groups and the items within them.
    const byGroup = (items: KeyValuePair<TKey>[]) => {
        const groups = new Map<string, KeyValuePair<TKey>[]>();
        for (const item of items) {
            const name = item.group ?? '';
            const existing = groups.get(name);
            if (existing) existing.push(item);
            else groups.set(name, [item]);
        }
        return [...groups.entries()];
    };

    const renderItem = (item: KeyValuePair<TKey>) => {
        const labelId = `transfer-list-item-${item.key}-label`;

        return (
            <ListItemButton
                key={item.key}
                role="listitem"
                onClick={handleToggle(item)}
            >
                <ListItemIcon>
                    <Checkbox
                        checked={isChecked(item)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{
                            'aria-labelledby': labelId,
                        }}
                    />
                </ListItemIcon>
                <ListItemText id={labelId} primary={item.value.toString()} />
            </ListItemButton>
        );
    };

    const customList = (items: KeyValuePair<TKey>[]) => (
        <Paper sx={{ width: listWidth, height: listHeight, overflow: 'auto' }}>
            <List dense component="div" role="list">
                {items.some(i => i.group)
                    ? byGroup(items).map(([group, groupItems]) => (
                        <Fragment key={group}>
                            <ListSubheader disableSticky sx={{ lineHeight: '2rem' }}>{group}</ListSubheader>
                            {groupItems.map(renderItem)}
                        </Fragment>
                    ))
                    : items.map(renderItem)}
            </List>
        </Paper>
    );

    return (
        <Grid
            container
            spacing={2}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
        >
            <Grid>{customList(left)}</Grid>
            <Grid>
                <Grid container direction="column" sx={{ alignItems: 'center' }}>
                    <Button
                        sx={{ minWidth: 1 }}
                        variant="outlined"
                        fullWidth
                        onClick={handleAllRight}
                        disabled={left.length === 0}
                        aria-label="move all right"
                    >
                        <KeyboardDoubleArrowRightIcon />
                    </Button>
                    <Spacer />
                    <Button
                        sx={{ minWidth: 1 }}
                        variant="outlined"
                        fullWidth
                        onClick={handleCheckedRight}
                        disabled={leftChecked.length === 0}
                        aria-label="move selected right"
                    >
                        <KeyboardArrowRightIcon />
                    </Button>
                    <Spacer />
                    <Button
                        sx={{ minWidth: 1 }}
                        variant="outlined"
                        fullWidth
                        onClick={handleCheckedLeft}
                        disabled={rightChecked.length === 0}
                        aria-label="move selected left"
                    >
                        <KeyboardArrowLeftIcon />
                    </Button>
                    <Spacer />
                    <Button
                        sx={{ minWidth: 1 }}
                        variant="outlined"
                        fullWidth
                        onClick={handleAllLeft}
                        disabled={right.length === 0}
                        aria-label="move all left"
                    >
                        <KeyboardDoubleArrowLeftIcon />
                    </Button>
                </Grid>
            </Grid>
            <Grid>{customList(right)}</Grid>
        </Grid>
    );
}

export default TransferList;